import type { OrderPaymentAmountPrepaidEvent } from "@@/shared/orders/order-events/events/order-payment-events/order-payment-amount-prepaid.event";
import {
    isOrderPaymentAmountPrepaidEvent,
    orderEventPaymentAmountPrepaidZodObject,
} from "@@/shared/orders/order-events/events/order-payment-events/order-payment-amount-prepaid.event";
import type { OrderPaymentCardGuaranteeEvent } from "@@/shared/orders/order-events/events/order-payment-events/order-payment-card-guarantee.event";
import {
    isOrderPaymentCardGuaranteeEvent,
    orderEventPaymentCardGuaranteeZodObject,
} from "@@/shared/orders/order-events/events/order-payment-events/order-payment-card-guarantee.event";
import type { OrderPaymentVirtualCardRegisteredEvent } from "@@/shared/orders/order-events/events/order-payment-events/order-payment-virtual-card-registered.event";
import {
    isOrderPaymentVirtualCardRegisteredEvent,
    orderEventVirtualCardRegisteredZodObject,
} from "@@/shared/orders/order-events/events/order-payment-events/order-payment-virtual-card-registered.event";
import z from "zod";
import type { OrderPaymentAmountCapturableUpdatedEvent } from "./order-payment-amount-capturable-updated.event";
import {
    isOrderPaymentAmountCapturableUpdatedEvent,
    orderEventPaymentAmountCapturableUpdatedZodObject,
} from "./order-payment-amount-capturable-updated.event";
import type { OrderPaymentAmountReleasedEvent } from "./order-payment-amount-released.event";
import {
    isOrderPaymentAmountReleasedEvent,
    orderEventPaymentAmountReleasedZodObject,
} from "./order-payment-amount-released.event";
import type { OrderPaymentFailedEvent } from "./order-payment-failed.event";
import {
    isOrderPaymentFailedEvent,
    orderEventPaymentFailedZodObject,
} from "./order-payment-failed.event";
import type { OrderPaymentFulfilledEvent } from "./order-payment-fulfilled.event";
import {
    isOrderPaymentFulfilledEvent,
    orderPaymentFulfilledEventZodObject,
} from "./order-payment-fulfilled.event";
import type { OrderPaymentPendingEvent } from "./order-payment-pending.event";
import {
    isOrderPaymentPendingEvent,
    orderEventPaymentPendingZodSchema,
} from "./order-payment-pending.event";
import type { OrderPaymentRefundFailedEvent } from "./order-payment-refund-failed.event";
import {
    isOrderPaymentRefundFailedEvent,
    orderEventPaymentRefundFailedZodObject,
} from "./order-payment-refund-failed.event";
import type { OrderPaymentRefundFulfilledEvent } from "./order-payment-refund-fulfilled.event";
import {
    isOrderPaymentRefundFulfilledEvent,
    orderPaymentRefundFulfilledEventZodObject,
} from "./order-payment-refund-fulfilled.event";
import type { OrderPaymentRefundInitiatedEvent } from "./order-payment-refund-initiated.event";
import {
    isOrderPaymentRefundInitiatedEvent,
    orderEventPaymentRefundInitiatedZodObject,
} from "./order-payment-refund-initiated.event";
import type { OrderPaymentRefundPendingEvent } from "./order-payment-refund-pending.event";
import {
    isOrderPaymentRefundPendingEvent,
    orderEventPaymentRefundPendingZodObject,
} from "./order-payment-refund-pending.event";

type OrderPaymentEvent =
    | OrderPaymentFailedEvent
    | OrderPaymentFulfilledEvent
    | OrderPaymentPendingEvent
    | OrderPaymentAmountCapturableUpdatedEvent
    | OrderPaymentAmountReleasedEvent
    | OrderPaymentRefundFailedEvent
    | OrderPaymentRefundFulfilledEvent
    | OrderPaymentRefundInitiatedEvent
    | OrderPaymentRefundPendingEvent
    | OrderPaymentAmountPrepaidEvent
    | OrderPaymentCardGuaranteeEvent
    | OrderPaymentVirtualCardRegisteredEvent;

const orderPaymentEventZodUnion = z
    .discriminatedUnion("type", [
        orderEventPaymentFailedZodObject,
        orderPaymentFulfilledEventZodObject,
        orderEventPaymentRefundFailedZodObject,
        orderPaymentRefundFulfilledEventZodObject,
        orderEventPaymentRefundInitiatedZodObject,
        orderEventPaymentRefundPendingZodObject,
        orderEventPaymentAmountCapturableUpdatedZodObject,
        orderEventPaymentAmountReleasedZodObject,
        orderEventPaymentAmountPrepaidZodObject,
        orderEventPaymentCardGuaranteeZodObject,
        orderEventVirtualCardRegisteredZodObject,
    ])
    .or(orderEventPaymentPendingZodSchema);

const orderPaymentEventZodSchema: z.ZodSchema<OrderPaymentEvent> =
    orderPaymentEventZodUnion;

const isOrderPaymentEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderPaymentEvent => {
    return (
        isOrderPaymentFailedEvent(event) ||
        isOrderPaymentFulfilledEvent(event) ||
        isOrderPaymentPendingEvent(event) ||
        isOrderPaymentAmountCapturableUpdatedEvent(event) ||
        isOrderPaymentAmountReleasedEvent(event) ||
        isOrderPaymentRefundFailedEvent(event) ||
        isOrderPaymentRefundFulfilledEvent(event) ||
        isOrderPaymentRefundInitiatedEvent(event) ||
        isOrderPaymentRefundPendingEvent(event) ||
        isOrderPaymentAmountPrepaidEvent(event) ||
        isOrderPaymentCardGuaranteeEvent(event) ||
        isOrderPaymentVirtualCardRegisteredEvent(event)
    );
};

export {
    isOrderPaymentEvent,
    orderPaymentEventZodSchema,
    orderPaymentEventZodUnion,
};
export type { OrderPaymentEvent };
